import React, { useCallback } from 'react';
import { Table } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import colors from '../../../constants/Colors';

import BudgetTrackingEmptyState from './BudgetTrackingEmptyState';
import WageConfigurationForm from './WageConfigurationForm';
import { renderDefaultColumn } from '../budgetTrackingHelpers';

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    render: renderDefaultColumn,
  },
  {
    title: 'Code',
    dataIndex: 'code',
    key: 'code',
    render: renderDefaultColumn,
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    render: renderDefaultColumn,
  },
];

// Configure Wage Rates Table
export default function ConfigureWageRatesTable({
  dataSource,
  wageConfigurations,
  onWageChange,
  expandedRowKeys,
  onExpandedRowsChange,
}) {
  const expandedRowRender = useCallback((record) => (
    <WageConfigurationForm
      record={record}
      wageValues={wageConfigurations[record.id] || {}}
      onWageChange={onWageChange}
    />
  ), [wageConfigurations, onWageChange]);

  const onExpand = useCallback((expanded, record) => {
    if (expanded) {
      onExpandedRowsChange([...expandedRowKeys, record.id]);
    } else {
      onExpandedRowsChange(expandedRowKeys.filter((key) => key !== record.id));
    }
  }, [expandedRowKeys, onExpandedRowsChange]);

  const expandIcon = useCallback(({ expanded, onExpand: handleExpand, record }) => (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => handleExpand(record, e)}
      onKeyDown={(e) => e.key === 'Enter' && handleExpand(record, e)}
      style={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
      }}
    >
      {expanded ? (
        <DownOutlined style={{ fontSize: 12, color: colors.GRAY_800 }} />
      ) : (
        <RightOutlined style={{ fontSize: 12, color: colors.GRAY_800 }} />
      )}
    </div>
  ), []);

  const emptyState = (
    <BudgetTrackingEmptyState text="No cost codes selected. Go back and select cost codes first." />
  );

  return (
    <Table
      className="budget-tracking-table budget-tracking-wage-configure"
      style={{ marginTop: 16 }}
      size="small"
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      rowKey="id"
      showHeader={false}
      expandable={{
        expandedRowRender,
        expandedRowKeys,
        onExpand,
        expandIcon,
        rowExpandable: () => true,
      }}
      scroll={{ x: 'max-content', y: 400 }}
      locale={{
        emptyText: emptyState,
      }}
      rowClassName="budget-tracking-wage-configure-row"
    />
  );
}

ConfigureWageRatesTable.propTypes = {
  dataSource: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    code: PropTypes.string,
    description: PropTypes.string,
  })),
  wageConfigurations: PropTypes.objectOf(PropTypes.shape({
    hourlyWage: PropTypes.number,
    dailyWage: PropTypes.number,
    wageAdjustment: PropTypes.number,
    wageMultiplier: PropTypes.number,
    hourlyBillingRate: PropTypes.number,
    dailyBillingRate: PropTypes.number,
  })),
  onWageChange: PropTypes.func,
  expandedRowKeys: PropTypes.arrayOf(PropTypes.string),
  onExpandedRowsChange: PropTypes.func,
};

ConfigureWageRatesTable.defaultProps = {
  dataSource: [],
  wageConfigurations: {},
  onWageChange: () => {},
  expandedRowKeys: [],
  onExpandedRowsChange: () => {},
};
